import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FixedMenu from '../../../Common/Menus/FixedMenu.jsx';
import { isValidClassName, normalizeClassList } from '../../utilities/htmlEditorUtils.js';
import { BOOTSTRAP_CLASS_SUGGESTIONS, isKnownBootstrapClass } from '../../utilities/styleFields.js';
import { FONT_THEME_CLASS } from '../../utilities/googleFonts.js';
import {
  ANIMATION_CLASSES,
  ANIMATION_CLASS_GROUPS,
  isEditorAnimationClass,
} from '../../utilities/animationClasses.js';

const ClassChoice = ({
  className,
  selected,
  custom,
  label = className,
  detail = '',
  iconClass = '',
  onToggle,
  onStyle,
}) => (
  <div className={`class-selector-choice ${custom ? 'has-style-action' : ''}`}>
    <button
      className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={() => onToggle(className)}
      title={selected ? `Remove ${className}` : `Apply ${className}`}
    >
      <i
        className={`bi ${selected ? 'bi-x-lg' : iconClass || 'bi-plus-lg'} me-2`}
        aria-hidden="true"
      ></i>
      <span className="class-selector-choice-copy">
        <span>{label}</span>
        {detail ? <small>{detail}</small> : null}
        {label !== className ? <code>{className}</code> : null}
      </span>
    </button>
    {custom ? (
      <button
        className="btn btn-sm btn-outline-primary class-selector-style-button"
        type="button"
        onClick={() => onStyle(className)}
        title={`Style .${className}`}
        aria-label={`Style ${className}`}
      >
        <i className="bi bi-brush" aria-hidden="true"></i>
      </button>
    ) : null}
  </div>
);

ClassChoice.propTypes = {
  className: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  custom: PropTypes.bool.isRequired,
  label: PropTypes.string,
  detail: PropTypes.string,
  iconClass: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onStyle: PropTypes.func.isRequired,
};

const ClassSelectorMenu = ({
  expanded,
  setExpanded,
  appliedClasses = [],
  customClasses = {},
  onToggleClass,
  onCreateCustomClass,
  onOpenClassStyles,
}) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState('custom');
  const [newCustomClass, setNewCustomClass] = useState('');
  const [error, setError] = useState('');
  const selectedClasses = normalizeClassList(appliedClasses)
    .filter((className) => className !== FONT_THEME_CLASS);
  const customClassNames = Object.keys(customClasses)
    .filter((className) => className !== FONT_THEME_CLASS)
    .filter((className) => !selectedClasses.includes(className))
    .sort();
  const firstSelectedCustomClass = selectedClasses.find((className) => customClasses[className]) || '';

  useEffect(() => {
    if (!expanded) {
      setQuery('');
      setActiveTab('custom');
      setNewCustomClass('');
      setError('');
    }
  }, [expanded]);

  const matchesQuery = (className) => className.toLowerCase().includes(query.trim().toLowerCase());
  const bootstrapClasses = normalizeClassList([...BOOTSTRAP_CLASS_SUGGESTIONS, ...selectedClasses])
    .filter((className) => !customClasses[className])
    .filter((className) => !selectedClasses.includes(className))
    .sort();

  const createCustomClass = () => {
    const className = newCustomClass.trim();
    if (!isValidClassName(className) || className === FONT_THEME_CLASS) {
      setError('Enter a valid custom class name.');
      return;
    }

    if (customClasses[className]) {
      setError('That custom class already exists. Select it from the list.');
      return;
    }

    if (isKnownBootstrapClass(className)) {
      setError('That name belongs to a Bootstrap class. Select it from the Bootstrap list.');
      return;
    }
    if (isEditorAnimationClass(className)) {
      setError('That name belongs to an Animation. Select it from the Animation tab.');
      return;
    }

    setError('');
    onCreateCustomClass(className);
    setNewCustomClass('');
  };

  const renderChoices = (classes, custom) => {
    const visibleClasses = classes.filter(matchesQuery);
    if (visibleClasses.length === 0) {
      return <p className="text-muted small mb-0">No matching classes.</p>;
    }

    return (
      <div className="class-selector-grid">
        {visibleClasses.map((className) => (
          <ClassChoice
            className={className}
            selected={selectedClasses.includes(className)}
            custom={custom}
            onToggle={onToggleClass}
            onStyle={onOpenClassStyles}
            key={className}
          />
        ))}
      </div>
    );
  };

  const renderAnimationChoices = (groupId) => {
    const choices = ANIMATION_CLASSES
      .filter(({ group }) => group === groupId)
      .filter(({ className, label, description }) => (
        matchesQuery(`${className} ${label} ${description}`)
      ));
    if (!choices.length) {
      return <p className="text-muted small mb-0">No matching animations.</p>;
    }
    return (
      <div className="class-selector-grid class-selector-animation-grid">
        {choices.map((choice) => (
          <ClassChoice
            {...choice}
            selected={selectedClasses.includes(choice.className)}
            custom={false}
            onToggle={onToggleClass}
            onStyle={onOpenClassStyles}
            key={choice.className}
          />
        ))}
      </div>
    );
  };

  return (
    <FixedMenu
      className="new-blog-fixed-menu"
      animate
      title="Select Classes"
      iconClass="bi-ui-checks-grid"
      expanded={expanded}
      setExpanded={setExpanded}
    >
      {error ? <div className="alert alert-danger small">{error}</div> : null}

      <section className="mb-4">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h6 className="class-selector-heading mb-0">
            Selected classes <span className="badge text-bg-primary ms-1">{selectedClasses.length}</span>
          </h6>
          <button
            className="btn btn-sm btn-outline-primary"
            type="button"
            onClick={() => onOpenClassStyles(firstSelectedCustomClass)}
          >
            <i className="bi bi-brush me-1" aria-hidden="true"></i>Class editor
          </button>
        </div>
        {selectedClasses.length ? (
          <div className="class-selector-grid">
            {selectedClasses.map((className) => (
              <ClassChoice
                className={className}
                selected
                custom={Boolean(customClasses[className])}
                onToggle={onToggleClass}
                onStyle={onOpenClassStyles}
                key={className}
              />
            ))}
          </div>
        ) : <p className="text-muted small mb-0">No classes selected.</p>}
      </section>

      <label className="form-label small fw-semibold" htmlFor="class-selector-search">Find a class</label>
      <div className="input-group input-group-sm mb-3">
        <span className="input-group-text"><i className="bi bi-search" aria-hidden="true"></i></span>
        <input
          id="class-selector-search"
          className="form-control"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search available classes"
        />
      </div>

      <div className="class-selector-tabs mb-3" role="tablist" aria-label="Class categories">
        {[
          ['custom', 'Custom', 'bi-brush'],
          ['bootstrap', 'Bootstrap', 'bi-bootstrap'],
          ['animation', 'Animation', 'bi-stars'],
        ].map(([tabId, label, iconClass]) => (
          <button
            className={`btn btn-sm ${activeTab === tabId ? 'btn-primary' : 'btn-outline-primary'}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tabId}
            onClick={() => setActiveTab(tabId)}
            key={tabId}
          >
            <i className={`bi ${iconClass} me-1`} aria-hidden="true"></i>{label}
          </button>
        ))}
      </div>

      {activeTab === 'custom' ? (
        <>
          <section className="mb-4">
            <h6 className="class-selector-heading">Custom classes</h6>
            {renderChoices(customClassNames, true)}
          </section>
          <section>
            <h6 className="class-selector-heading">Create custom class</h6>
            <div className="input-group input-group-sm">
              <input
                className="form-control"
                value={newCustomClass}
                onChange={(event) => setNewCustomClass(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    createCustomClass();
                  }
                }}
                placeholder="New custom class"
              />
              <button className="btn btn-outline-success" type="button" onClick={createCustomClass}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>Create
              </button>
            </div>
          </section>
        </>
      ) : null}

      {activeTab === 'bootstrap' ? (
        <section>
          <h6 className="class-selector-heading">Bootstrap classes</h6>
          {renderChoices(bootstrapClasses, false)}
        </section>
      ) : null}

      {activeTab === 'animation' ? ANIMATION_CLASS_GROUPS.map((group) => (
        <section className="mb-4" key={group.id}>
          <h6 className="class-selector-heading">{group.label}</h6>
          {renderAnimationChoices(group.id)}
        </section>
      )) : null}
    </FixedMenu>
  );
};

ClassSelectorMenu.propTypes = {
  expanded: PropTypes.bool.isRequired,
  setExpanded: PropTypes.func.isRequired,
  appliedClasses: PropTypes.arrayOf(PropTypes.string),
  customClasses: PropTypes.object,
  onToggleClass: PropTypes.func.isRequired,
  onCreateCustomClass: PropTypes.func.isRequired,
  onOpenClassStyles: PropTypes.func.isRequired,
};

export default ClassSelectorMenu;
